<script setup lang="ts">
import {
  SITE_URL,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildLocalBusinessSchema,
  buildFAQSchema,
  serializeJsonLd,
  type BusinessProfile,
} from '~/utils/schemaOrg'

definePageMeta({
  layout: 'default'
})

const { t, te, locale } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const runtimeConfig = useRuntimeConfig()
const { fetchFaqs } = useFaqAPI()

const businessProfile: BusinessProfile = {
  streetAddress: (runtimeConfig.public.businessRegisteredAddress as string) || undefined,
  phone: (runtimeConfig.public.businessPhone as string) || undefined,
  email: (runtimeConfig.public.businessEmail as string) || undefined,
  addressLocality: (runtimeConfig.public.businessAddressLocality as string) || undefined,
  addressRegion: (runtimeConfig.public.businessAddressRegion as string) || undefined,
  addressCountry: (runtimeConfig.public.businessAddressCountry as string) || undefined,
  priceRange: (runtimeConfig.public.businessPriceRange as string) || undefined,
}

useSeoMeta({
  title: () => t('seo.home.title'),
  description: () => t('seo.home.description'),
  ogTitle: () => t('seo.home.title'),
  ogDescription: () => t('seo.home.description'),
  ogType: 'website',
  ogUrl: () => `${SITE_URL}${localePath('/')}`,
  twitterCard: 'summary_large_image',
  twitterTitle: () => t('seo.home.title'),
  twitterDescription: () => t('seo.home.description'),
})

// --- Hreflang ---
const HREFLANG_MAP = {
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-PT',
  zh: 'zh-CN',
  fr: 'fr-FR',
} as const
type HreflangLocale = keyof typeof HREFLANG_MAP

const canonicalUrl = computed(() => `${SITE_URL}${localePath('/')}`)

const hreflangLinks = computed(() => {
  const codes = Object.keys(HREFLANG_MAP) as HreflangLocale[]
  const links = codes.map(code => ({
    rel: 'alternate' as const,
    hreflang: HREFLANG_MAP[code] as string,
    href: `${SITE_URL}${switchLocalePath(code) || (code === 'es' ? '/' : `/${code}`)}`,
  }))
  // x-default → Spanish (default locale, no prefix)
  links.push({
    rel: 'alternate' as const,
    hreflang: 'x-default',
    href: `${SITE_URL}/`,
  })
  return links
})

function fallbackFaqs() {
  const items: Array<{ question: string; answer: string }> = []
  for (let n = 1; n <= 12; n++) {
    const qk = `faq.q${n}.question`
    const ak = `faq.q${n}.answer`
    if (!te(qk) || !te(ak)) break
    items.push({
      question: t(qk) as string,
      answer: t(ak) as string,
    })
  }
  return items
}

// The visible accordion and FAQPage JSON-LD share this exact SSR payload. If
// the CMS is unavailable, both use the same localized source fallback rather
// than replacing one of them after hydration.
const { data: faqData } = await useAsyncData(
  () => `home-faqs:${locale.value}`,
  async () => {
    try {
      const items = await fetchFaqs({ limit: 6 })
      if (items.length > 0) {
        return items.map(item => ({ question: item.question, answer: item.answer }))
      }
    } catch {
      // The localized fallback keeps the homepage useful during an API outage.
    }
    return fallbackFaqs()
  },
  { watch: [locale] },
)
const visibleFaqs = computed(() => faqData.value?.length ? faqData.value : fallbackFaqs())
const faqSchema = computed(() => {
  if (visibleFaqs.value.length === 0) return null
  return buildFAQSchema(visibleFaqs.value)
})

useHead({
  link: () => [
    { rel: 'canonical', href: canonicalUrl.value },
    ...hreflangLinks.value,
  ],
  script: () => {
    const scripts: Array<{ type: string; innerHTML: string }> = [
      {
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(buildOrganizationSchema(locale.value, businessProfile)),
      },
      {
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(buildWebSiteSchema(locale.value)),
      },
      {
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(buildLocalBusinessSchema(locale.value, businessProfile)),
      },
    ]
    if (faqSchema.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(faqSchema.value),
      })
    }
    return scripts
  },
})
</script>

<template>
  <div class="min-h-screen bg-white dark:bg-gray-950">
    <!-- 1. Hero Section - Main value proposition -->
    <HeroSection />

    <!-- 2. Stable shopping discovery for the primary buyer persona -->
    <CategoryQuickLinks />

    <!-- 3. Weekly campaign: promotional content never replaces the brand H1 -->
    <WeeklyDealSection />

    <!-- 4. Newest products are server-rendered with a stable fallback -->
    <LazyVisibleFeaturedProducts hydrate-on-visible />

    <!-- 5. Secondary journeys: buy/sell/repair -->
    <LazyVisibleValuePropTrifecta hydrate-on-visible />

    <!-- 6. Instant Trade-In Calculator -->
    <LazyVisibleTradeInCalculator hydrate-on-visible />

    <!-- Below-the-fold sections defer hydration until scrolled into view
         (hydrate-on-visible). They still server-render for SEO; only their JS
         execution is delayed, which cuts initial main-thread work / TBT. -->

    <!-- 7. Trust & service information -->
    <LazyVisibleTrustCertifications hydrate-on-visible />

    <!-- 8. How It Works - Tabbed Interface -->
    <LazyVisibleHowItWorksTabbed hydrate-on-visible />

    <!-- 9. Blog – latest articles -->
    <LazyVisibleBlogSection hydrate-on-visible />

    <!-- 10. FAQ Section -->
    <LazyVisibleFAQSection :faqs="visibleFaqs" hydrate-on-visible />
  </div>
</template>
